import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import Logo from "../components/Logo";
import VideoPlayer from "../components/VideoPlayer";
import Reveal from "../components/Reveal";
import BookCallPopup from "../components/BookCallPopup";
import { IMAGES, HOME_TESTIMONIALS, ABOUT_TEXT, YOUTUBE } from "../mock";

export default function Home() {
  return (
    <div>
      <BookCallPopup />
      {/* Hero: centered brand mark, mirrors original landing */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#effaf9] to-white" />
        <div className="relative max-w-5xl mx-auto px-5 pt-16 pb-12 md:pt-24 md:pb-16 flex flex-col items-center text-center">
          <Reveal>
            <Logo />
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 text-xs md:text-sm tracking-[0.28em] uppercase text-neutral-500">
              Click here to connect with Coach Kam
            </p>
          </Reveal>
          <Reveal delay={200} className="w-full">
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <Link to="/book" className="btn-brand btn-teal">
                Book a Call
              </Link>
              <Link to="/services" className="btn-brand btn-outline-teal">
                Explore Services
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Video */}
      <section className="max-w-4xl mx-auto px-5 py-10 md:py-14">
        <Reveal>
          <VideoPlayer />
          <p className="text-center text-neutral-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            {YOUTUBE.caption}
          </p>
        </Reveal>
      </section>

      {/* Signature quote */}
      <section className="bg-[var(--teal)] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="font-serif-brand text-white text-3xl md:text-5xl leading-snug italic">
              "Holistic health is about the whole you, mind, body, soul and spirit."
            </p>
            <p className="text-white/85 mt-6 tracking-[0.3em] uppercase text-sm">- Kamla</p>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <Reveal className="text-center mb-14">
          <h2 className="font-serif-brand text-4xl md:text-5xl text-purple">What Others Are Saying</h2>
          <div className="brand-rule mx-auto mt-4" />
        </Reveal>
        <div className="grid gap-10 md:grid-cols-3">
          {HOME_TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 120}>
              <div className="h-full bg-white rounded-lg border border-neutral-100 shadow-sm hover:shadow-xl transition-shadow duration-300 p-7 flex flex-col">
                <div className="flex items-center gap-4 mb-5">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-[var(--teal)]"
                  />
                  <Quote className="text-[var(--teal)] ml-auto" size={28} />
                </div>
                <p className="text-neutral-600 text-[0.95rem] leading-relaxed flex-1">{t.quote}</p>
                <p className="mt-6 text-purple font-semibold tracking-wide-sm text-sm uppercase">
                  {t.name}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About Kamla */}
      <section className="bg-[#f7fbfa] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-[var(--teal)]/40 rounded-lg" />
              <img
                src={IMAGES.kamlaAbout}
                alt="Kamla Das"
                className="relative rounded-lg shadow-xl w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-serif-brand text-4xl md:text-5xl text-purple leading-tight">
              Hello, I'm Kamla Das
            </h2>
            <div className="brand-rule mt-4 mb-6" />
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              {ABOUT_TEXT.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <Link to="/services" className="btn-brand btn-purple mt-8">
              Learn more about our services <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA row */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-20">
        <div className="grid gap-4 md:grid-cols-3 text-center">
          {[
            { label: "Services", to: "/services", filled: false },
            { label: "Astrology", to: "/astrology", filled: false },
            { label: "Book a Call", to: "/book", filled: true },
          ].map((c, i) => (
            <Reveal key={c.label} delay={i * 100}>
              {c.filled ? (
                <Link
                  to={c.to}
                  className="block py-8 rounded-lg bg-[var(--teal)] text-white hover:bg-[var(--teal-dark)] hover:-translate-y-1 transition-all duration-300 font-semibold tracking-[0.2em] uppercase text-sm"
                >
                  {c.label}
                </Link>
              ) : (
                <Link
                  to={c.to}
                  className="block py-8 rounded-lg border border-neutral-200 hover:border-[var(--teal)] hover:-translate-y-1 transition-all duration-300 text-purple font-semibold tracking-[0.2em] uppercase text-sm"
                >
                  {c.label}
                </Link>
              )}
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
